import React from "react";
import { BlockMath } from "react-katex";
import { Rich, Paragraphs } from "./Rich";

const H = ({ children }) => <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, color: "#7c2d12", marginBottom: 6 }}>{children}</div>;
const Box = ({ children, border = "#e5e7eb", bg = "#fff" }) => (
  <div className="pdf-avoid" style={{ border: `1px solid ${border}`, background: bg, borderRadius: 8, padding: 12, marginBottom: 0 }}>{children}</div>
);
const List = ({ items, ordered }) => {
  const Tag = ordered ? "ol" : "ul";
  return <Tag style={{ paddingLeft: 20, margin: 0, listStyle: ordered ? "decimal" : "disc" }}>{items.map((s, i) => <li key={i} style={{ marginBottom: 4 }}><Rich>{s}</Rich></li>)}</Tag>;
};

const P = {
  overview: (b) => <div style={{ marginBottom: 0 }}><Paragraphs items={b.body} /></div>,
  prerequisites: (b) => <Box border="#bae6fd" bg="#f0f9ff"><H>Before you start</H><List items={b.items} /></Box>,
  concept: (b) => (
    <div className="pdf-avoid" style={{ borderLeft: "3px solid #0891b2", paddingLeft: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}><Rich>{b.title}</Rich></div>
      <Paragraphs items={b.body} />
    </div>
  ),
  formula: (b) => (
    <Box border="#fde68a" bg="#fffbeb">
      <H>Formula · <Rich>{b.title}</Rich></H>
      <BlockMath math={b.latex} renderError={() => <code>{b.latex}</code>} />
      <div><Rich>{b.explanation}</Rich></div>
      {b.when_to_use && <div style={{ fontSize: 12, color: "#57534e", marginTop: 4 }}>When to use: <Rich>{b.when_to_use}</Rich></div>}
    </Box>
  ),
  theorem: (b) => (
    <Box border="#ddd6fe" bg="#f5f3ff">
      <H>Theorem · <Rich>{b.title}</Rich></H>
      <div style={{ fontStyle: "italic", marginBottom: 6 }}><Rich>{b.statement}</Rich></div>
      {b.proof?.length > 0 && <><div style={{ fontWeight: 600, fontSize: 12 }}>Proof</div><List items={b.proof} ordered /></>}
    </Box>
  ),
  example: (b) => (
    <Box>
      <H>Example{b.title ? " · " : ""}<Rich>{b.title || ""}</Rich> ({b.difficulty})</H>
      <div style={{ fontWeight: 600, marginBottom: 6 }}><Rich>{b.question}</Rich></div>
      <List items={b.steps} ordered />
      <div style={{ marginTop: 6, color: "#065f46", fontWeight: 600 }}>Answer: <Rich>{b.answer}</Rich></div>
    </Box>
  ),
  mistake: (b) => (
    <Box border="#fecaca" bg="#fef2f2">
      <H>Common mistake</H>
      <div>✗ <Rich>{b.wrong}</Rich></div>
      <div>✓ <Rich>{b.right}</Rich></div>
      <div style={{ fontSize: 12, color: "#57534e", marginTop: 4 }}>Why: <Rich>{b.why}</Rich></div>
    </Box>
  ),
  revision: (b) => <Box border="#a7f3d0" bg="#ecfdf5"><H>Quick revision</H><List items={b.items} ordered /></Box>,
  exam_focus: (b) => <Box border="#fbcfe8" bg="#fdf2f8"><H>Exam focus</H><Paragraphs items={b.body} /><div style={{ marginTop: 6 }}><List items={b.items} /></div></Box>,
  check_question: (b) => (
    <Box border="#a5f3fc" bg="#ecfeff">
      <H>Check yourself</H>
      <div style={{ marginBottom: 4 }}><Rich>{b.question}</Rich></div>
      {b.options?.length > 0 && <List items={b.options.map((o, i) => `(${"abcd"[i]}) ${o}`)} />}
      <div style={{ marginTop: 6, fontSize: 12 }}><b>Answer:</b> <Rich>{b.answer}</Rich> — <Rich>{b.explanation}</Rich></div>
    </Box>
  ),
  visual: (b) => (
    <div className="pdf-avoid" style={{ textAlign: "center", color: "#1f2937" }}>
      <div style={{ maxWidth: 380, margin: "0 auto" }} dangerouslySetInnerHTML={{ __html: b.svg }} />
      <div style={{ fontSize: 11, color: "#6b7280" }}><Rich>{b.caption}</Rich></div>
    </div>
  ),
};

const SECTION = { width: 760, padding: "4px 8px", background: "#fff", color: "#1f2937", fontSize: 13, lineHeight: 1.55, fontFamily: "Georgia, 'Times New Roman', serif" };

export const PrintableNotes = ({ notes }) => (
  <div data-testid="printable-notes">
    {notes.topics.map((t, i) => (
      <section key={t.id} className="pdf-topic" style={SECTION}>
        <div className="pdf-item" data-break="true" style={{ background: "#fff", paddingBottom: 4 }}>
          {i === 0 && (
            <div style={{ marginBottom: 16, borderBottom: "2px solid #1f2937", paddingBottom: 8 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "#9d174d" }}>Gyaan Maths · Class {notes.class_level} · Chapter notes</div>
              <div style={{ fontSize: 26, fontWeight: 700 }}>{notes.chapter_title}</div>
            </div>
          )}
          <div style={{ fontSize: 18, fontWeight: 700, margin: "4px 0 2px" }}>{String(i).padStart(2, "0")} · <Rich>{t.title}</Rich></div>
          <div style={{ fontSize: 12, color: "#6b7280" }}><Rich>{t.summary}</Rich></div>
        </div>
        {t.blocks.map((b) => <div key={b.id} className="pdf-item [&_svg]:w-full [&_svg]:h-auto" style={{ background: "#fff" }}>{P[b.type]?.(b)}</div>)}
      </section>
    ))}
  </div>
);
